// One editable rate on the Referees tab: a referee's default rate, or the rate
// of a single game. The Firestore subscription is the source of truth — the
// typed value lives here only until the snapshot answers

import { useEffect, useRef, useState } from 'react';
import InputMoney from './InputMoney';
import './RefereeRateInput.scss';

export default function RefereeRateInput({
  value,
  isDefault,
  onSet,
  onClear,
  disabled,
}) {
  // `null` means "showing the saved value"
  const [draft, setDraft] = useState(null);
  const isFocused = useRef(false);
  // Reactive twin of the ref above, used only to drop the gray "default" look
  // the instant the field is focused, rather than waiting on the write to
  // round-trip through Firestore before `isDefault` recomputes
  const [focused, setFocused] = useState(false);

  // The snapshot has landed, so whatever was typed has served its purpose —
  // unless it is still being typed. Every game cell inheriting the referee's
  // default sees `value` change when that default is saved, and one of them may
  // be the field the admin has already moved on to
  useEffect(() => {
    if (!isFocused.current) setDraft(null);
  }, [value]);

  const saved = value === undefined ? '' : String(value);

  // The amount shows at once and is rolled back by whoever holds it if the
  // write fails — reported there too, so a rejection only means the draft has
  // outlived its purpose
  const save = async (action) => {
    try {
      await action();
    } catch {
      setDraft(null);
    }
  };

  // Clearing can be declined — the referee rate asks first — and the field has
  // to come back to the saved value either way
  const clear = async () => {
    await save(onClear);
    setDraft(null);
  };

  const commit = () => {
    isFocused.current = false;
    setFocused(false);
    if (draft === null) return setDraft(null);
    // A cell showing the referee's default owns no amount to give back, and
    // neither does a field that was empty before it was typed into
    if (draft === '')
      return isDefault || value === undefined ? setDraft(null) : clear();

    const amount = +draft;
    // A lone separator is a half-typed amount, not a number
    if (!Number.isFinite(amount)) return setDraft(null);
    // Repeating the amount a cell only inherits is a decision, not a no-op: it
    // pins the game at that rate, where before it followed the referee's
    // default. Compared as numbers, so `50.00` over a saved `50` is not a write
    if (!isDefault && saved !== '' && amount === +saved) return setDraft(null);

    return save(() => onSet(amount));
  };

  return (
    <InputMoney
      placeholder='Rate'
      className={'rate-input' + (isDefault && !focused ? ' default' : '')}
      value={draft ?? saved}
      onChange={setDraft}
      onFocus={() => {
        isFocused.current = true;
        setFocused(true);
      }}
      onBlur={commit}
      // Dropping the draft is enough on Escape — blurring would fire the commit
      // handler of this render, which still closes over the value just discarded
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') setDraft(null);
      }}
      // Only an amount of its own can be cleared: a game showing the referee's
      // default has nothing to give back
      onClear={value !== undefined && !isDefault ? clear : undefined}
      disabled={disabled}
      small
    />
  );
}
